import React, { FC } from "react";
import { I18n } from "react-redux-i18n";
import { FormControl } from "@common/components/form-control";
import { Checkbox } from "@common/components/checkbox";

import Dropdown from "@common/containers/fields/Dropdown";
import { SkipLogicConditionListDto, SkipLogicConditionOperator } from "@common/components/skip-logic/skip-logic.types";
import { getTestAttributes } from "@common/utils/tests";
import { FieldType } from "@common/dto/field/type";
import { ConditionalCheckboxValue } from "./type";

import './conditional-field.styles.scss';

type ConditionalFieldProps = {
  condition: SkipLogicConditionListDto;
  onChange: (value: string | number | boolean) => void;
  moduleName: string;
};

export const ConditionalField: FC<ConditionalFieldProps> = ({ condition, onChange, moduleName }) => {
  const [localValue, setLocalValue] = React.useState<string>("");

  const conditionOptions = React.useMemo(
    () =>
      (condition.field.options || []).map((field) => ({
        value: field.actualValue,
        label: field.displayValue,
      })),
    [condition.field.options]
  );

  React.useEffect(() => {
    if (condition.value !== localValue) {
      setLocalValue(condition.value ?? "");
    }
  }, [condition.value]);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleDropdownChange = React.useCallback(
    (val: string) => {
      onChange(val);
    },
    [onChange]
  );

  const handleCheckboxChange = React.useCallback(
    (checked: boolean) => {
      onChange(checked ? ConditionalCheckboxValue.checked : ConditionalCheckboxValue.unchecked);
    },
    [onChange]
  );

  const handleTextChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const getCurrentField = (fieldTypeId: number) => {

    const checkboxCanHaveOptions = fieldTypeId != FieldType.checkbox;

    const isValidOperatorForTextAndDateFields = [
      SkipLogicConditionOperator.beginsWith,
      SkipLogicConditionOperator.contains,
      SkipLogicConditionOperator.doesNotBeginWith,
      SkipLogicConditionOperator.doesNotContain,
      SkipLogicConditionOperator.doesNotEndWith,
      SkipLogicConditionOperator.endsWith,
      SkipLogicConditionOperator.in,
      SkipLogicConditionOperator.isNotIn,
      SkipLogicConditionOperator.isNotLike,
      SkipLogicConditionOperator.like,
    ].includes(condition.operator as SkipLogicConditionOperator);

    const isValidFieldTypeForTextAndDateFields = [FieldType.date, FieldType.computed, FieldType.dropdown].includes(condition.field.type);

    const dropdownValue = conditionOptions.find(condition => condition.value.toString().toLowerCase() === localValue.toLowerCase())?.value;
    
    if (isValidOperatorForTextAndDateFields && isValidFieldTypeForTextAndDateFields) {
      return (
        <FormControl
          {...getTestAttributes(moduleName, "skip-logic-conditional-field", "text")}
          value={localValue}
          className="SkipLogic__conditional-field"
          type="text"
          onChange={handleChange}
          onBlur={handleTextChange}
        />
      );
    }


    if (conditionOptions.length && checkboxCanHaveOptions) {
      return (
        <Dropdown
          {...getTestAttributes(moduleName, "skip-logic-conditional-field", "dropdown")}
          placeholder={I18n.t("Select")}
          className="SkipLogic__conditional-field SkipLogic__conditional-field--dropdown"
          selectedValue={dropdownValue}
          searchable
          options={conditionOptions}
          onValueChanged={handleDropdownChange}
        />
      );
		}

    switch (fieldTypeId) {
      case FieldType.system:
      case FieldType.table:
      case FieldType.dropdown:
        return (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__conditional-field SkipLogic__conditional-field--dropdown"
            selectedValue={dropdownValue}
            searchable
            options={conditionOptions}
            onValueChanged={handleDropdownChange}
          />
        );
      case FieldType.checkbox:
        return (
          <Checkbox
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "checkbox")}
            checked={localValue === ConditionalCheckboxValue.checked}
            className="SkipLogic__conditional-field"
            onChange={handleCheckboxChange}
          />
        );
      case FieldType.number:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "number")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="number"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
      case FieldType.date:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "date")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="date"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
      default:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "text")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="text"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
    }
  };

  return getCurrentField(condition.field.type);
};
